import { Navigate } from 'react-router'
import type { ReactNode } from 'react'
import { ErrorState, FullPageLoading } from '../components/states'
import { CenteredFrame } from '../components/AppFrame'
import { SignInScreen } from '../screens/SignInScreen'
import { useAuth } from './AuthProvider'

/**
 * The single gate in front of every authenticated surface.
 *
 * The ordering here is the point of the whole component: NOTHING gated renders
 * until `session-gate` has returned 200. `checking` deliberately renders a
 * full-page loader rather than the app frame with a spinner in it — showing the
 * frame first and discovering the verdict second is the exact failure this
 * guards against.
 *
 * When there is no session, the sign-in screen renders IN PLACE of the
 * requested route rather than redirecting to a `/sign-in` URL. That is what
 * makes the return URL simply "where you already are": a rep landing cold on
 * /sku/ABC123 from a Script Manager deep link sees sign-in at that URL, and
 * signing in leaves them exactly there.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { state, emailCode, providerError, signInWithProvider, retry } = useAuth()

  switch (state.status) {
    case 'initializing':
      return <FullPageLoading label="Loading…" />

    case 'checking':
      return <FullPageLoading label="Checking your access…" />

    case 'signed_out':
      return (
        <SignInScreen
          onProvider={signInWithProvider}
          emailCode={emailCode}
          providerError={providerError}
        />
      )

    case 'not_authorized':
      return <Navigate to="/not-authorized" replace />

    case 'error':
      return (
        <CenteredFrame>
          <ErrorState
            title="We could not check your access"
            detail={state.detail}
            onRetry={retry}
          />
        </CenteredFrame>
      )

    case 'authorized':
      return <>{children}</>
  }
}
